"use client";

import { useEffect, useState } from "react";
import { fetchAllCityData } from "./serverDataFetcher";

/**
 * Client-side data hydration component for City pages
 * This component runs ONLY in the browser after the static HTML is loaded
 * It fetches fresh GraphQL data in the background
 *
 * NOTE: This doesn't update the server component directly.
 * Instead, it stores fresh data that interactive client components can access.
 * For displaying GraphQL data, child components should be made client components
 * that listen for this hydration.
 *
 * Following the same pattern as Neighborhood ClientDataHydration
 */
export default function CityDataHydration({ citySlug, initialData }) {
  const [isHydrating, setIsHydrating] = useState(false);
  const [hydrationComplete, setHydrationComplete] = useState(false);

  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined") return;

    // Only fetch if we have initial data to compare against
    if (!initialData || !citySlug) return;

    // Don't fetch if already hydrating or complete
    if (isHydrating || hydrationComplete) return;

    // Fetch GraphQL data in the background
    const hydrateWithGraphQL = async () => {
      try {
        setIsHydrating(true);
        console.log(`🔄 [CLIENT] Starting GraphQL hydration for city: ${citySlug}`);

        const graphqlData = await fetchAllCityData(citySlug);

        if (graphqlData && graphqlData?.result && graphqlData?.result?.data) {
          console.log(`✅ [CLIENT] GraphQL data fetched successfully for city`);

          // Merge GraphQL data with initial JSON data
          const mergedData = {
            ...initialData.data,
            ...graphqlData.result.data,
            // Ensure all required fields exist
            getcityreview: graphqlData.result.data?.getcityreview || initialData.data?.getcityreview || [],
            getcityimage: graphqlData.result.data?.getcityimage || initialData.data?.getcityimage || [],
            getcitydata: graphqlData.result.data?.getcitydata || initialData.data?.getcitydata || [],
            getcityscore: graphqlData.result.data?.getcityscore || initialData.data?.getcityscore || [],
            getcityquestions: graphqlData.result.data?.getcityquestions || initialData.data?.getcityquestions || [],
            getschoolincity: graphqlData.result.data?.getschoolincity || initialData.data?.getschoolincity || []
          };

          // Store the hydrated data in window object for access by other components
          if (typeof window !== "undefined") {
            window.__CITY_HYDRATED_DATA__ = {
              citySlug,
              data: mergedData,
              timestamp: Date.now()
            };

            // Dispatch custom event so components can listen and update
            const event = new CustomEvent('city-data-hydrated', {
              detail: { citySlug, data: mergedData }
            });
            window.dispatchEvent(event);
          }

          setHydrationComplete(true);
          console.log(`✅ [CLIENT] City hydration complete and broadcasted for: ${citySlug}`);
        } else {
          console.log(`⚠️  [CLIENT] GraphQL returned no data, using initial JSON data`);
          setHydrationComplete(true);
        }
      } catch (err) {
        console.error(`❌ [CLIENT] GraphQL hydration failed:`, err);
        // Keep using initial JSON data on error
        setHydrationComplete(true);
      } finally {
        setIsHydrating(false);
      }
    };

    // Start hydration after a small delay to ensure initial render is complete
    const timer = setTimeout(() => {
      hydrateWithGraphQL();
    }, 100);

    return () => clearTimeout(timer);
  }, [citySlug, initialData, isHydrating, hydrationComplete]);

  // Optionally show a small loading indicator while hydrating
  if (isHydrating) {
    return (
      <div style={{ display: 'none' }} data-hydrating="true" aria-hidden="true">
        Updating with latest data...
      </div>
    );
  }

  return null;
}
